function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function normalizeOpacity(value) {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : undefined;
}

export function normalizeLayerHighlightColor(value) {
  if (!Array.isArray(value) || value.length !== 3) {
    return undefined;
  }
  return value.every(
    (component) =>
      typeof component === "number"
      && Number.isInteger(component)
      && component >= 0
      && component <= 255,
  )
    ? value.slice()
    : undefined;
}

export function normalizeLayerHighlightRecord(value) {
  if (
    !isPlainObject(value)
    || Object.keys(value).length !== 3
    || !hasOwn(value, "enabled")
    || !hasOwn(value, "opacity")
    || !hasOwn(value, "color")
    || typeof value.enabled !== "boolean"
  ) {
    return undefined;
  }
  const opacity = normalizeOpacity(value.opacity);
  const color = normalizeLayerHighlightColor(value.color);
  if (opacity === undefined || color === undefined) {
    return undefined;
  }
  return {
    enabled: value.enabled,
    opacity,
    color,
  };
}

export function normalizeLayerHighlightMap(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [key, value] of Object.entries(values)) {
    const layerId = String(key ?? "").trim();
    const highlight = normalizeLayerHighlightRecord(value);
    if (layerId && highlight) {
      out[layerId] = highlight;
    }
  }
  return out;
}

export function layerHighlightColorValue(color) {
  const normalized = normalizeLayerHighlightColor(color);
  if (!normalized) {
    return "";
  }
  return `#${normalized.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

export function parseLayerHighlightColor(value) {
  const normalized = String(value ?? "").trim();
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(normalized);
  return match
    ? match.slice(1).map((channel) => Number.parseInt(channel, 16))
    : undefined;
}

export function layerHighlightRecordsEqual(left, right) {
  const normalizedLeft = normalizeLayerHighlightRecord(left);
  const normalizedRight = normalizeLayerHighlightRecord(right);
  return Boolean(
    normalizedLeft
      && normalizedRight
      && normalizedLeft.enabled === normalizedRight.enabled
      && Math.abs(normalizedLeft.opacity - normalizedRight.opacity) <= 0.0001
      && normalizedLeft.color.every(
        (channel, index) => channel === normalizedRight.color[index],
      ),
  );
}
